import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { listMyShortcutFavorites, toggleShortcutFavorite } from '../lib/shortcut-favorites';

interface AtajosRevitModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal pantalla completa que muestra el dashboard de atajos (KS) de Revit
 * en un iframe. El HTML standalone vive en /public/atajos-revit/index.html y
 * trae sus propias fuentes, estilos y datos (116 atajos en 9 categorías).
 *
 * Layout en pantallas chicas (< 1366×768):
 *   El iframe tiene min-width 1366 + min-height 768, y el contenedor scrollea.
 *   El HTML interno tiene un canvas fijo de 1920×1080 con auto-scale por
 *   `Math.min(window.innerWidth/1920, window.innerHeight/1080)`. Si el iframe
 *   nunca baja de 1366×768, el scale interno nunca cae debajo de ~0.71 → el
 *   contenido siempre se ve legible. El alumno usa scroll si su viewport
 *   es más chico.
 *
 * El botón "Cerrar" se renderiza FUERA del contenedor scrolleable, en
 * posición fixed al viewport, así no se mueve cuando el alumno scrollea
 * dentro del modal.
 *
 * Portal:
 *   AlumnoDashboard tiene `animate-slide-up` (transform) en su contenedor
 *   raíz. Un ancestor con transform convierte `position: fixed` en relativo
 *   a ese ancestor. Renderizando vía createPortal en document.body nos
 *   saltamos toda la cadena de transforms y el fixed se ancla al viewport
 *   real, cubriendo el sidebar también.
 */
export function AtajosRevitModal({ open, onClose }: AtajosRevitModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Set en React (state-ref) con los códigos favoritos del usuario.
  // Lo usamos como source-of-truth para mandar al iframe via postMessage.
  const favsRef = useRef<Set<string>>(new Set());
  // Trigger interno: cuando el iframe pide "ready-favs" antes de tener los
  // datos cargados, levantamos este flag para reenviarle apenas cargan.
  const [iframeReady, setIframeReady] = useState(false);

  // Cerrar con Escape + bloquear scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Carga inicial de favoritos cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    (async () => {
      const codes = await listMyShortcutFavorites();
      favsRef.current = codes;
      postInitToIframe();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cuando el iframe avisa "ready", reenviamos los favoritos
  useEffect(() => {
    if (!open) return;
    if (iframeReady) postInitToIframe();
  }, [open, iframeReady]);

  function postInitToIframe() {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'init-favs', codes: Array.from(favsRef.current) },
        '*',
      );
    } catch {}
  }

  // Listener a mensajes del iframe: ready-favs y toggle-fav
  useEffect(() => {
    if (!open) return;
    const handler = async (ev: MessageEvent) => {
      // Aceptamos mensajes solo si vienen del iframe actual
      if (iframeRef.current && ev.source !== iframeRef.current.contentWindow) return;
      const m: any = ev.data;
      if (!m || typeof m !== 'object') return;

      if (m.type === 'ready-favs') {
        setIframeReady(true);
        return;
      }
      if (m.type === 'toggle-fav' && typeof m.code === 'string') {
        const code = String(m.code).toUpperCase();
        const wasFav = favsRef.current.has(code);
        // Sincronizamos optimistamente el set local con el state del iframe
        if (m.isFav) favsRef.current.add(code);
        else         favsRef.current.delete(code);
        // Persistir en Supabase — si falla, revertimos y reavisamos al iframe
        const result = await toggleShortcutFavorite(code, wasFav);
        if (result === null) {
          // revertir
          if (wasFav) favsRef.current.add(code);
          else        favsRef.current.delete(code);
          postInitToIframe();
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Contenedor scrolleable con el iframe.
          - overflow-auto: si el iframe excede el viewport (porque tiene
            min-width 1366 / min-height 768), aparecen scrolls.
          - El iframe usa 100% como tamaño "deseado" pero respeta el mínimo. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Atajos de Revit"
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-auto scroll-cubus"
      >
        <iframe
          ref={iframeRef}
          src="/atajos-revit/index.html"
          title="Atajos de Revit — CUBUS"
          onLoad={() => postInitToIframe()}
          className="block border-0"
          style={{
            width: '100%',
            height: '100vh',
            minWidth: '1366px',
            minHeight: '768px',
          }}
          // allow-scripts: el buscador y los chips de categoría usan JS.
          // allow-same-origin: necesario para que el path relativo del logo
          // (cubus-logo.png) se resuelva contra /atajos-revit/.
          sandbox="allow-scripts allow-same-origin"
        />
      </div>

      {/* Botón Cerrar — fixed al viewport, fuera del contenedor scrolleable,
          así no se mueve cuando el alumno arrastra el contenido lateral
          o verticalmente. z-index mayor que el contenedor. */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[110] group flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 hover:border-cubus-lime/40 backdrop-blur-md transition-all focus-lime"
        title="Cerrar (Esc)"
        aria-label="Cerrar atajos"
      >
        <span className="text-sm font-medium text-white/80 group-hover:text-white">Cerrar</span>
        <X className="w-4 h-4 text-white/60 group-hover:text-cubus-lime transition-colors" />
      </button>
    </>,
    document.body,
  );
}
